import { isEqual, isEqualWith } from 'lodash'

import { isObject, isUndefinedOrNull } from './checks'
import { omitBy, pick } from './lang'

export type ObjectPath = PropertyKey | ReadonlyArray<PropertyKey> | null

/**
 * Retorna o valor de um caminho aninhado, aceitando notação por pontos,
 * colchetes ou um array de chaves. O valor padrão só é usado para `undefined`.
 */
export function get<T = unknown>(object: unknown, path: ObjectPath, defaultValue?: T): T | undefined {
  const value = resolvePath(object, path).reduce<unknown>((current, key) => {
    if (current == null) return undefined
    return Object(current)[key]
  }, object)

  return value === undefined ? defaultValue : (value as T)
}

function resolvePath(object: unknown, path: ObjectPath): PropertyKey[] {
  const isKey = typeof path === 'string' || typeof path === 'number' || typeof path === 'symbol'
  if (isKey && object != null && path in Object(object)) return [path]
  return toPath(path)
}

function toPath(path: ObjectPath): PropertyKey[] {
  if (path === null) return ['null']
  if (typeof path !== 'string') {
    return typeof path === 'number' || typeof path === 'symbol' ? [path] : [...path]
  }

  const keys: string[] = []
  if (path.startsWith('.')) keys.push('')
  path.replace(
    /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g,
    (match, number, quote, quotedValue) => {
      keys.push(quote ? quotedValue.replace(/\\(\\)?/g, '$1') : number ?? match)
      return match
    }
  )
  return keys
}

/**
 * Verifica se todos os valores do objeto estão nulos, vazios ou falsos em busca profunda
 * @param obj Objeto a ser verificado
 */
export function isObjectDeepFalsy(obj: object | string): boolean {
  if (!obj) return true

  if (obj instanceof Set || obj instanceof Map || Array.isArray(obj)) {
    return ![...obj.values()].some((item) => !isObjectDeepFalsy(item))
  }

  if (typeof obj === 'object') {
    return !Object.values(obj).some((value) => !isObjectDeepFalsy(value))
  }

  return false
}

/**
 * Verifica se todos os valores do objeto estão nulos ou vazios
 * @param obj Objeto a ser verificado
 */
export function isObjectDeepEmpty(obj: object | string): boolean {
  if (isUndefinedOrNull(obj)) return true

  if (obj instanceof Set || obj instanceof Map || Array.isArray(obj)) {
    return ![...obj].some((item) => !isObjectDeepEmpty(item))
  }

  if (typeof obj === 'object') {
    return !Object.values(obj).some((value) => !isObjectDeepEmpty(value))
  }

  return false
}

function hasSameId(a: unknown, b: unknown) {
  const firstId = getIdentifiable(a)?.id
  const secondId = getIdentifiable(b)?.id
  if (!isUndefinedOrNull(firstId) && firstId === secondId) {
    return true
  }
}

function getIdentifiable(value: unknown): { id?: unknown } | undefined {
  return isObject(value) ? value : undefined
}

/**
 * Verifica se ambos os objetos são iguais em busca profunda, considerando árvores de mesmo `id` como iguais
 * @param a Objeto a ser comparado
 * @param b Objeto a ser comparado
 */
export function isObjectDeepEqualById(a: unknown, b: unknown) {
  return hasSameId(a, b) || isEqualWith(a, b, hasSameId)
}

/**
 * Reimplementação nativa do `isBuffer` do lodash: verifica se o valor é um
 * `Buffer` do Node. No bundle do navegador o global `Buffer` normalmente não
 * existe, então o resultado é `false` — mesmo comportamento do lodash nesse cenário.
 */
function isBuffer(value: unknown): boolean {
  const bufferCtor = (globalThis as { Buffer?: { isBuffer(v: unknown): boolean } }).Buffer
  return typeof bufferCtor?.isBuffer === 'function' && bufferCtor.isBuffer(value)
}

/**
 * Retorna um objeto com as keys "planificadas"
 * Adaptado de: https://github.com/hughsk/flat
 * @param target Objeto a ser "planificado"

 */
export function flatten(target: object) {
  const delimiter = '.'
  const output: Record<string, unknown> = {}

  function step(object: object, prev?: string) {
    Object.keys(object).forEach(function (key) {
      const value = (object as Record<string, unknown>)[key]
      const type = Object.prototype.toString.call(value)
      const isFlattenable = type === '[object Object]' || type === '[object Array]'

      const newKey = prev ? prev + delimiter + key : key

      if (!isBuffer(value) && isFlattenable && Object.keys(value as object).length) {
        return step(value as object, newKey)
      }

      output[newKey] = value
    })
  }

  step(target)

  return output
}

export function flattenKeys(object: object, keysToRemove: string[] = []): string[] {
  const flattened = flatten(object)

  return Object.keys(flattened)
    .map((key) =>
      key
        .split('.')
        .filter((k) => !keysToRemove.includes(k))
        .join('.')
    )
    .filter((key) => key !== '')
}

/**
 * Remove todas as propriedades de um objeto em que o predicado retorna verdadeiro.
 * @param object Objeto a ser "limpo"
 * @param predicate Função que determina se uma propriedade deve ser removida ou não. Se retornar true, significa que o campo será removido do objeto.
 * @returns O objeto sem as propriedades em que a predicate retorna verdadeiro
 */
export function deepOmitBy<T extends object>(object: T, predicate: (value: unknown) => boolean): Partial<T> {
  if (Array.isArray(object) || isUndefinedOrNull(object)) {
    return object
  }

  const newObject = omitBy<T>(object, predicate)

  Object.entries(newObject)
    .filter(([_, value]) => isObject(value) && !Array.isArray(value))
    .forEach(([key, value]) => ((newObject as Record<string, unknown>)[key] = deepOmitBy(value as object, predicate)))

  return newObject
}

export function matchesWithPartial<T extends object>(currentValues: T, defaultValues: Partial<T>): boolean {
  const relevantKeys = Object.keys(defaultValues)
  const currentRelevantValues = pick(currentValues, relevantKeys)

  const cleanedDefaults = deepOmitBy(defaultValues, isUndefinedOrNull)
  const cleanedCurrent = deepOmitBy(currentRelevantValues, isUndefinedOrNull)

  return isEqual(cleanedDefaults, cleanedCurrent)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!isObject(value) || Array.isArray(value)) return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function mergeSourceInto(target: Record<string, unknown>, source: Record<string, unknown>) {
  Object.keys(source).forEach((key) => {
    const srcValue = source[key]
    if (srcValue === null || srcValue === undefined) return
    if (isPlainObject(srcValue)) {
      const base = isPlainObject(target[key]) ? target[key] : {}
      mergeSourceInto(base, srcValue)
      target[key] = base
    } else {
      target[key] = srcValue
    }
  })
}

/**
 * Realiza um merge profundo entre objetos, ignorando campos nulos ou undefined da origem.
 * Arrays são substituídos e não mesclados. Objetos aninhados (planos) são clonados,
 * portanto o resultado é sempre um NOVO objeto que não compartilha referências mutáveis
 * com o `object` base.
 * @param object Objeto base (default)
 * @param sources Objetos de origem (que sobrescreverão o base)
 * @returns Um NOVO objeto combinado
 */
export function mergeDeepIgnoreNulls<T>(object: T, ...sources: Partial<T>[]): T {
  const result: Record<string, unknown> = {}
  ;[object, ...sources].forEach((source) => {
    if (isPlainObject(source)) mergeSourceInto(result, source)
  })
  return result as T
}
